import Map from 'ol/map'
import View from 'ol/view'
import Observable from 'ol/observable'
import Geolocation from 'ol/geolocation'
import Feature from 'ol/feature'
import Geometry from 'ol/geom/geometry'
import Overlay from 'ol/overlay'
import Style from 'ol/style/style'
import CircleStyle from 'ol/style/circle'
import FillStyle from 'ol/style/fill'
import StrokeStyle from 'ol/style/stroke'
import TextStyle from 'ol/style/text'
import IconStyle from 'ol/style/icon'
import RegularShapeStyle from 'ol/style/regularshape'
import PointGeom from 'ol/geom/point'
import BaseLayer from 'ol/layer/base'
import VectorLayer from 'ol/layer/vector'
import VectorSource from 'ol/source/vector'
import Interaction from 'ol/interaction/interaction'
import SelectInteraction from 'ol/interaction/select'
import Control from 'ol/control/control'
import GlonassJSONFormat from './Format/OL3/GlonassJSSON'
import GeoJSONFormat from 'ol/format/geojson'
import extent from 'ol/extent'
import proj from 'ol/proj'
import proj4 from 'proj4'
import 'ol/ol.css'
import { createTileUrlFunction } from 'ol-tilecache'
import uuid from 'node-uuid'
import styleOptions from '../styleOptions'
import { InvalidArgumentError } from '../../errors'
import 'Data/Cache'
import '../Geometry'
import '../LayerType'
import './Base'

proj.setProj4(proj4)

/** @namespace GIS.Map */
NS("GIS.Map");
/**
 * @class
 * @extends GIS.Map.Base
 */
GIS.Map.OL3 = Class(GIS.Map.Base, /** @lends GIS.Map.OL3.prototype */ {
    /**
     * @type {string}
     */
    vendor: "ol3",
    /**
     * @protected
     * @type {ol.Map}
     */
    map: null,
    /**
     * @protected
     * @type {ol.View}
     */
    view: null,
    /**
     * @protected
     * @type {Object.<string, ol.interaction.Interaction>}
     */
    interactions_: {},
    /**
     * @protected
     * @type {Object.<string, ol.control.Control>}
     */
    controls_: {},
    /**
     * @protected
     * @type {Object.<string, ol.Overlay>}
     */
    overlays_: {},
    /**
     * @protected
     * @type {Object.<string, ol.layer.Base>}
     */
    layers_: {},
    /**
     * @protected
     * @type {ol.Geolocation}
     */
    geoloc_: null,
    /**
     * @type {ol.layer.Vector}
     * @protected
     */
    geolocLayer: null,
    /**
     * @protected
     * @type {ol.Feature}
     */
    accuracyFeature: null,
    /**
     * @protected
     * @type {ol.Feature}
     */
    positionFeature: null,
    /**
     * @protected
     * @type {Object.<string, ol.EventsKey>}
     */
    evtListeners: {},
    /**
     * @returns {number[]|undefined}
     */
    get currentPosition() {
        return this.getCurrentPosition();
    },

    // options
    center: [0, 0],
    zoom: 1,
    maxZoom: 22,
    minZoom: 0,
    geoloc: false,
    geolocMarker: true,
    renderer: 'canvas',
    /**
     * @type {Element}
     */
    keyboardEventTarget: undefined,
    controls: {
        attribution: true,
        zoom: false,
        mapScale: true
    },
    /**
     * @param {Element|$.<Element>} container
     * @param {Object} [options]
     */
    initialize(container, options) {
        this.Parent(container, options);

        if (this.geoloc) {
            this.initGeolocation();
        }

        if (this.keyboardEventTarget) {
            this.keyboardEventTarget.addEventListener("keydown", this);
            this.keyboardEventTarget.addEventListener("keyup", this);
        }
    },
    /**
     * @param {Object} options
     */
    prepareOptions(options) {
        this.Parent(options);

        _.extend(options, Util.pickOptions(this, [
            'center', 'maxZoom', 'minZoom', 'projection', 'zoom',
            'controls', 'keyboardEventTarget', 'renderer',
        ]))
    },
    /**
     * @returns {ol.Map}
     */
    getVendor() {
        return this.map;
    },
    /**
     * Initialize Geo location API
     * @returns {GIS.Map.OL3}
     */
    initGeolocation() {
        this.geoloc_ = new Geolocation({
            projection: this.getProjection()
        });

        this.startTracking();

        return this;
    },
    /**
     * @returns {boolean}
     */
    startTracking() {
        if (!this.geoloc_ || this.geoloc_.getTracking()) {
            return false;
        }

        this.geoloc_.setTracking(true);

        if (this.geolocMarker) {
            // geolocation features
            this.accuracyFeature = new Feature({
                geoloc: true
            });
            listen.call(this, "accuracyFeature.geometry", this.geoloc_, "change:accuracyGeometry", (e) => {
                this.accuracyFeature.setGeometry(e.target.getAccuracyGeometry());
            });

            this.positionFeature = new Feature({
                geoloc: true
            });
            this.positionFeature.setStyle(new Style({
                image: new CircleStyle({
                    radius: 6,
                    fill: new FillStyle({
                        color: [51, 153, 204, 0.5]
                    }),
                    stroke: new StrokeStyle({
                        color: [255, 255, 255, 0.7],
                        width: 2
                    })
                })
            }));
            listen.call(this, "positionFeature.geometry", this.geoloc_, "change:position", (e) => {
                this.positionFeature.setGeometry(new PointGeom(e.target.getPosition()));
            });

            this.geolocLayer = new VectorLayer({
                map: this.map,
                source: new VectorSource({
                    features: [this.accuracyFeature, this.positionFeature]
                })
            });
        }

        return true;
    },
    /**
     * @returns {boolean}
     */
    startFollowing() {
        if (!this.geoloc_ || !this.geoloc_.getTracking()) {
            return false;
        }

        listen.call(this, "geoloc.position", this.geoloc_, "change:position", () => {
            this.setCenter(GIS.Map.Base.toGeoJSON(this.geoloc_.getPosition(), GIS.Geometry.Point));
        });
        this.geoloc_.changed();

        return true;
    },
    /**
     * @returns {boolean}
     */
    stopTracking() {
        if (!this.geoloc_ || !this.geoloc_.getTracking()) {
            return false;
        }

        this.stopFollowing();
        this.geoloc_.setTracking(false);

        if (this.geolocMarker) {
            _.forEach(_.pick(this.evtListeners, ["accuracyFeature.geometry", "positionFeature.geometry"]), unlisten.bind(this));
            this.accuracyFeature = null;
            this.positionFeature = null;

            this.geolocLayer.setMap(null);
            this.geolocLayer = null;
        }

        return true;
    },
    /**
     * @returns {boolean}
     */
    stopFollowing() {
        if (!this.geoloc_ || !this.geoloc_.getTracking()) {
            return false;
        }

        unlisten.call(this, "geoloc.position");

        return true;
    },
    /**
     * @returns {GIS.Map.OL3}
     */
    deinitGeolocation() {
        this.stopTracking();
        this.geoloc_ = null;

        return this;
    },
    /**
     * @returns {number[]|undefined}
     */
    getCurrentPosition() {
        return this.geoloc_ && this.geoloc_.getPosition();
    },
    /**
     * @returns {GIS.Map.OL3}
     */
    destroy() {
        this.deinitGeolocation();

        _.forEach(this.overlays_, _.rearg(this.removeOverlay, 1, 0).bind(this));
        _.forEach(this.interactions_, _.rearg(this.removeInteraction, 1, 0).bind(this));
        _.forEach(this.controls_, _.rearg(this.removeControl, 1, 0).bind(this));
        _.forEach(this.evtListeners, unlisten.bind(this));

        if (this.keyboardEventTarget) {
            this.keyboardEventTarget.removeEventListener("keydown", this);
            this.keyboardEventTarget.removeEventListener("keyup", this);
        }

        this.view = null;
        this.map.setTarget(null);
        this.map = null;
        this.keyboardEventTarget = null;

        return this.ParentCall();
    },
    /**
     * @param {Element} container
     * @param {Object} [options]
     * @returns {ol.Map}
     * @protected
     */
    createMap(container, options = {}) {
        const mapOptions = _.extend(Util.pickOptions(options, ['keyboardEventTarget', 'renderer']), {
            target: container,
            controls: [],
        });
        const viewOptions = Util.pickOptions(options, ["center", "maxZoom", "minZoom", "projection", "zoom"]);

        mapOptions.view = this.view = new View(viewOptions);

        this.map = new Map(mapOptions);

        // bind map collections events
        listen.call(this, "overlay.add", this.map.getOverlays(), "add", createMapCollectionAddCallback(this.overlays_), this);
        listen.call(this, "interaction.add", this.map.getInteractions(), "add", createMapCollectionAddCallback(this.interactions_), this);
        listen.call(this, "control.add", this.map.getControls(), "add", createMapCollectionAddCallback(this.controls_), this);
        listen.call(this, "layer.add", this.map.getLayers(), "add", createMapCollectionAddCallback(this.layers_), this);

        listen.call(this, "overlay.remove", this.map.getOverlays(), "remove", createMapCollectionRemoveCallback(this.overlays_), this);
        listen.call(this, "interaction.remove", this.map.getInteractions(), "remove", createMapCollectionRemoveCallback(this.interactions_), this);
        listen.call(this, "control.remove", this.map.getControls(), "remove", createMapCollectionRemoveCallback(this.controls_), this);
        listen.call(this, "layer.remove", this.map.getLayers(), "remove", createMapCollectionRemoveCallback(this.layers_), this);

        // bind map events
        _.forEach(["singleclick", "moveend", "dblclick", "pointerdown", "pointermove", "pointerup"], (event) => {
            listen.call(this, "map." + event, this.map, event, this.handleEvent, this);
        });
        _.forEach(["change:resolution"], (event) => {
            listen.call(this, "view." + event, this.map.getView(), event, this.handleEvent, this);
        });

        // add controls
        // todo сейчас не используется, используются кастомные контролы
        // _.forEach(options.controls, (val, key) => {
        //     if (!key.match(/.*Options$/) && val) {
        //         this.addControl( key, new ol.control[ _.upperFirst( key ) ]( options.controls[ key + "Options" ] ) );
        //     }
        // });

        // add select interaction
        this.addInteraction("select", this.createSelectInteraction(options.interactionOptions && options.interactionOptions.select));

        return this.map;
    },
    /**
     * @param {string} name
     * @param {ol.interaction.Interaction} interaction
     * @returns {GIS.Map.OL3}
     */
    addInteraction(name, interaction) {
        interaction.name_ = name;
        this.map.addInteraction(interaction);

        return this;
    },
    /**
     * @param {string} name
     * @returns {GIS.Map.OL3}
     */
    removeInteraction(name) {
        this.map && this.map.removeInteraction(this.interactions_[name]);

        return this;
    },
    /**
     * @param {string} name
     * @returns {ol.interaction.Interaction|undefined}
     */
    getInteraction(name) {
        return this.interactions_[name];
    },
    /**
     * @param {string} name
     * @param {ol.Overlay} overlay
     * @returns {GIS.Map.OL3}
     */
    addOverlay(name, overlay) {
        overlay.name_ = name;
        this.map.addOverlay(overlay);

        return this;
    },
    /**
     * @param {string} name
     * @returns {GIS.Map.OL3}
     */
    removeOverlay(name) {
        this.map && this.map.removeOverlay(this.overlays_[name]);

        return this;
    },
    /**
     * @param {string} name
     * @returns {ol.Overlay|undefined}
     */
    getOverlay(name) {
        return this.overlays_[name];
    },
    /**
     * @param {string} name
     * @param {ol.layer.Base} layer
     * @returns {GIS.Map.OL3}
     */
    addVendorLayer(name, layer) {
        layer.name_ = name;
        this.map.addLayer(layer);

        return this;
    },
    /**
     * @param {string} name
     * @returns {GIS.Map.OL3}
     */
    removeVendorLayer(name) {
        this.map && this.map.removeLayer(this.layers_[name]);

        return this;
    },
    /**
     * @param {string} name
     * @returns {ol.layer.Base}
     */
    getVendorLayer(name) {
        return this.layers_[name];
    },
    /**
     * @param {string} name
     * @param {ol.control.Control} control
     * @returns {GIS.Map.OL3}
     */
    addControl(name, control) {
        control.name_ = name;
        this.map.addControl(control);

        return this;
    },
    /**
     * @param {string} name
     * @returns {GIS.Map.OL3}
     */
    removeControl(name) {
        this.map && this.map.removeControl(this.controls_[name]);

        return this;
    },
    /**
     * Returns vendor control if exists
     * @param {string} name
     * @returns {ol.control.Control|undefined}
     */
    getControl(name) {
        return this.controls_[name];
    },
    /**
     * @returns {ol.interaction.Select}
     * @protected
     */
    createSelectInteraction(options) {
        const defaultStyleFunction = createStyleFunction(this.getDefaultStyleHash());

        const select = new SelectInteraction(_.merge({
            filter: feature => (
                // true if feature at overlay except geo location features
                !_.includes([ this.accuracyFeature, this.positionFeature ], feature) &&
                // true if feature is not cluster or it is single element cluster or map at max zoom
                (
                    !isClusterFeature(feature) ||
                    feature.get("features").length === 1 ||
                    this.getZoom() === this.maxZoom
                )
            ),
            style: (feature) => {
                const layer = this.findLayer(
                    layer => layer.Is(GIS.Map.Layer.Vector) && !!layer.getAdapter().getFeature(feature.getId())
                )

                if (layer) {
                    return layer.getAdapter().styleFunction(feature);
                }

                return defaultStyleFunction(feature);
            }
        }, options));

        listen.call(this, "feature.select", select.getFeatures(), "add", this.onSelectFeature, this);
        listen.call(this, "feature.unselect", select.getFeatures(), "remove", this.onUnSelectFeature, this);

        return select;
    },
    /** @inheritDoc */
    align() {
        this.refresh();

        return this.ParentCall();
    },
    /**
     * @fires GIS.Map.OL3#refresh
     * @returns {GIS.Map.OL3}
     */
    refresh() {
        this.map.updateSize();
        // this.setCenter(this.getCenter()); // immediately triggers map refreshing
        this.map.render();

        return this.ParentCall();
    },
    /**
     * @param {Object|ol.geom.Geometry|number[]} geomJson Array of coordinates or geometry in GeoJSON
     * @param {Number} [zoom]
     * @param {olx.AnimationOptions} [options]
     * @returns {GIS.Map.OL3}
     */
    setCenter(geomJson, zoom, options = {}) {
        if (!_.isEmpty(geomJson)) {
            zoom || (zoom = this.getZoom())
            const center = Array.isArray(geomJson) ? geomJson : pointOnSurface(geomJson)

            this.view.animate({
                duration: 350,
                ...options,
                zoom,
                center
            })
        }

        return this;
    },
    /**
     * @returns {number[]|undefined}
     */
    getCenter() {
        return this.view.getCenter();
    },
    /**
     * @param {number} zoom
     * @param {olx.AnimationOptions} [options]
     * @returns {GIS.Map.OL3}
     */
    setZoom(zoom, options = {}) {
        this.view.animate({
            duration: 350,
            ...options,
            zoom
        });

        return this;
    },
    /**
     * @returns {number|undefined}
     */
    getZoom() {
        return this.view.getZoom();
    },
    /**
     * @returns {number|undefined}
     */
    getResolution() {
        return this.view.getResolution();
    },
    /**
     * @param {number} resolution
     * @param {olx.AnimationOptions} [options]
     * @returns {GIS.Map.OL3}
     */
    setResolution(resolution, options = {}) {
        this.view.animate({
            duration: 350,
            ...options,
            resolution
        });

        return this;
    },
    /**
     * @returns {ol.proj.ProjectionLike}
     */
    getProjection() {
        return this.view.getProjection();
    },
    /**
     * @param resolution
     * @param delta
     * @param dir
     * @returns {number|undefined}
     */
    constrainResolution(resolution, delta, dir) {
        return this.view.constrainResolution(resolution, delta, dir);
    },
    /**
     * @returns {Array.<number>|undefined}
     */
    getSize() {
        return this.map.getSize();
    },
    /**
     * @param {ol.geom.SimpleGeometry|ol.Extent|number[]} geom
     * @param {ol.Size|number[]} [size]
     * @param {olx.view.FitOptions} [options]
     * @returns {GIS.Map.OL3}
     */
    fit(geom, size = this.getSize(), options = {}) {
        geom = _.isPlainObject(geom) && geom.type ? this.geomFromGeoJSON(geom) : geom;
        this.view.fit(geom, {
            duration: 500,
            maxZoom: this.maxZoom,
            size,
            ...options
        });

        return this;
    },
    /**
     * @returns {Array.<number>}
     */
    getExtent() {
        return this.view.calculateExtent(this.map.getSize());
    },
    /**
     * @param {ol.MapBrowserEvent} e
     * @fires GIS.Map.OL3#click
     * @fires GIS.Map.OL3#dbl.click
     * @fires GIS.Map.OL3#zoom.end
     * @fires GIS.Map.OL3#move.end
     * @fires GIS.Map.OL3#pointer.down
     * @fires GIS.Map.OL3#pointer.move
     * @fires GIS.Map.OL3#pointer.up
     * @fires GIS.Map.OL3#key.down
     * @fires GIS.Map.OL3#key.up
     * @protected
     */
    handleEvent(e) {
        switch (e.type) {
            case "singleclick":
                /**
                 * @event GIS.Map.OL3#click
                 * @type {ol.MapBrowserEvent}
                 */
                this.emit("click", e);
                break;
            case "dblclick":
                /**
                 * @event GIS.Map.OL3#dbl.click
                 * @type {ol.MapBrowserEvent}
                 */
                this.emit("dbl.click", e);
                break;
            case "change:resolution":
                /**
                 * @event GIS.Map.OL3#zoom.end
                 * @type {ol.Object.Event}
                 */
                this.emit("zoom.end", e);
                break;
            case "moveend":
                /**
                 * @event GIS.Map.OL3#move.end
                 * @type {ol.MapEvent}
                 */
                this.emit("move.end", e);
                break;
            case "pointerdown":
                /**
                 * @event GIS.Map.OL3#pointer.down
                 * @type {ol.MapBrowserEvent}
                 */
                this.emit("pointer.down", e);
                break;
            case "pointermove":
                /**
                 * @event GIS.Map.OL3#pointer.move
                 * @type {ol.MapBrowserEvent}
                 */
                this.emit("pointer.move", e);
                break;
            case "pointerup":
                /**
                 * @event GIS.Map.OL3#pointer.up
                 * @type {ol.MapBrowserEvent}
                 */
                this.emit("pointer.up", e);
                break;
            case "keydown":
                /**
                 * @event GIS.Map.OL3#key.down
                 * @type {KeyboardEvent}
                 */
                this.emit("key.down", e);
                break;
            case "keyup":
                /**
                 * @event GIS.Map.OL3#key.up
                 * @type {KeyboardEvent}
                 */
                this.emit("key.up", e);
                break;
        }
    },
    /**
     * @param {number[]} pixel
     * @param {function(feature: ol.Feature, layer: ol.layer.Base)} iterator
     * @param {*} [context]
     * @param {function} [layerFilter]
     * @returns {T|undefined}
     */
    forEachFeatureAtPixel(pixel, iterator, context, layerFilter) {
        context || (context = this);

        return this.map.forEachFeatureAtPixel(pixel, iterator, context, layerFilter, context);
    },
    /**
     * @param {number[]} pixel
     * @param {function(layer: ol.layer.Base)} predicate
     * @param {*} [context]
     * @param {function} [layerFilter]
     * @returns {T|undefined}
     */
    forEachLayerAtPixel(pixel, predicate, context, layerFilter) {
        context || (context = this);

        return this.map.forEachLayerAtPixel(pixel, predicate, context, layerFilter, context);
    },
    /**
     * @param {function(a : GIS.Map.Layer.Base, b : GIS.Map.Layer.Base)} predicate
     * @param {*} [context]
     * @returns {GIS.Map.OL3}
     */
    sortLayers(predicate, context) {
        const layers = this.map.getLayers().getArray();
        const clickListeners = this.eventsSubscriptions["click"];

        _.chain(layers)
            .sort(
                /**
                 * Inversely sort ol3 layers.
                 * @param {ol.layer.Layer} layer1
                 * @param {ol.layer.Layer} layer2
                 * @returns {number}
                 */
                (layer1, layer2) => {
                    var res = Util.call(predicate, context || this, [layer1.get("glLayer"), layer2.get("glLayer")]);

                    return res === 0 ? 0 : res * -1;
                }
            )
            .forEach(_.partial(clickListenerMove, _, clickListeners).bind(this))
            .value();

        this.refresh();

        return this;
    },
    /**
     * @returns {GIS.Map.OL3}
     */
    zoomIn() {
        return this.zoomByDelta(1);
    },
    /**
     * @returns {GIS.Map.OL3}
     */
    zoomOut() {
        return this.zoomByDelta(-1);
    },
    /**
     * @param delta
     * @returns {GIS.Map.OL3}
     */
    zoomByDelta(delta) {
        this.setResolution(this.constrainResolution(this.getResolution(), delta));

        return this;
    },
    /**
     * @param {Array.<Number>} pixel
     * @returns {Array.<Number>} Returns lon lat in map projection
     */
    coordinateFromPixel(pixel) {
        return this.map.getCoordinateFromPixel(pixel);
    },
    /**
     * Adds Popup overlay on map.
     *
     * @fires GIS.Map.OL3#popup.show
     * @param {string|Element} content
     * @param {number[]} coordinate
     * @param {Object} [options] Additional data for popup
     * @returns {ol.Overlay|undefined}
     */
    openPopup(content, coordinate, options = {}) {
        options.name || (options.name = "popup");

        var popup = ( /** @type {ol.Overlay} */ this.getOverlay(options.name));

        if (!popup) {
            // todo заменить на простой ol.Overlay
            popup = new Overlay(options)
            // popup = new ol.Overlay.Popup({
            //     ...options,
            //     beforeShow(popup) {
            //         return Velocity.animate(popup.getElement(), {
            //             opacity: [ 1, 0 ],
            //             scaleX: [ 1, 0 ],
            //             scaleY: [ 1, 0 ]
            //         }, {
            //             duration: 350,
            //             visibility: 'visible'
            //         });
            //     },
            //     beforeHide(popup) {
            //         return Velocity.animate(popup.getElement(), 'reverse');
            //     }
            // });
            this.addOverlay(options.name, popup);
        } else {
            popup.setProperties(options);
        }

        popup.hideListener_ = popup.on("hide", ({ target }) => {
            /**
             * @event GIS.Map.OL3#popup.close
             * @type {{popup: ol.Overlay, coordinate: number[]}}
             */
            this.emit("popup.close", {
                popup: target,
                coordinate: target.getPosition()
            });

            if (options.name !== 'popup') {
                this.removeOverlay(options.name);

                Observable.unByKey(popup.hideListener_);
                delete popup.hideListener_;
            }
        });

        popup.show(coordinate, content);
        /**
         * @event GIS.Map.OL3#popup.show
         * @type {{popup: ol.Overlay, coordinate: number[]}}
         */
        this.emit("popup.open", {
            popup,
            coordinate: popup.getPosition()
        });

        return popup;
    },
    /**
     * Closes popup by name and removes it if needed
     *
     * @fires GIS.Map.OL3#popup.close
     * @param {ol.Overlay|string} [popup] Popup object or plain object with options
     * @returns {ol.Overlay|undefined}
     */
    closePopup(popup) {
        if (popup == null) {
            popup = this.getOverlay("popup");
        } else if (_.isString(popup)) {
            popup = this.getOverlay(popup);
        }

        if (popup) {
            this.map.removeOverlay(popup)
        }

        return popup;
    },
    /**
     * @param {ol.Feature} feature
     * @returns {boolean}
     */
    isSelectedFeature(feature) {
        const select = ( /** @type {ol.interaction.Select} */ this.getInteraction("select"));

        return select && _.includes(select.getFeatures().getArray(), feature);
    },
    /**
     * @fires GIS.Map.OL3#feature.select
     * @param {ol.Feature} feature
     * @returns {GIS.Map.OL3}
     */
    selectFeature(feature) {
        const select = ( /** @type {ol.interaction.Select} */ this.getInteraction("select"));

        if (select && !this.isSelectedFeature(feature)) {
            select.getFeatures().push(feature);
        }

        return this;
    },
    /**
     * @fires GIS.Map.OL3#feature.unselect
     * @param {ol.Feature} feature
     * @returns {GIS.Map.OL3}
     */
    unselectFeature(feature) {
        const select = ( /** @type {ol.interaction.Select} */ this.getInteraction("select"));

        if (select && this.isSelectedFeature(feature)) {
            select.getFeatures().remove(feature);
        }

        return this;
    },
    /**
     * @param {ol.Collection.Event} e
     * @protected
     */
    onSelectFeature(e) {
        const feature = ( /** @type {ol.Feature} */ e.element);
        setFeatureStyleName(feature, 'select')
        /**
         * @event GIS.Map.OL3#feature.select
         * @type {ol.Collection.Event}
         */
        this.emit("feature.select", {...e, feature});
    },
    /**
     * @param {ol.Collection.Event} e
     * @protected
     */
    onUnSelectFeature(e) {
        const feature = ( /** @type {ol.Feature} */ e.element);
        setFeatureStyleName(feature, getFeaturePrevStyleName(feature))
        feature.unset(GIS.Map.Base.PREV_STYLE_NAME_PROP);
        /**
         * @event GIS.Map.OL3#feature.unselect
         * @type {ol.Collection.Event}
         */
        this.emit("feature.unselect", {...e, feature});
    },
    /**
     * @param {ol.geom.Geometry} geometry
     * @param {ol.proj.ProjectionLike} [geometryProjection]
     * @param {ol.proj.ProjectionLike} [geoJSONProjection]
     * @param {boolean} [serialize=false]
     * @returns {Object|string}
     */
    geomToGeoJSON(geometry, geometryProjection = this.getProjection(), geoJSONProjection = this.displayProjection, serialize = false) {
        return geomToGeoJSON(
            geometry,
            geometryProjection,
            geoJSONProjection,
            serialize
        );
    },
    /**
     * @param {Object|string} geoJson
     * @param {ol.proj.ProjectionLike} [geometryProjection]
     * @param {ol.proj.ProjectionLike} [geoJSONProjection]
     * @returns {ol.geom.Geometry}
     */
    geomFromGeoJSON(geoJson, geometryProjection = this.getProjection(), geoJSONProjection = this.displayProjection) {
        return geomFromGeoJSON(
            geoJson,
            geometryProjection,
            geoJSONProjection
        );
    },

    // shared helper functions
    createTileUrlFunction: createTileUrlFunction,
    defineProjection,
    transformCoordinateProjection,
    transformExtentProjection,
    isEquivalentProjections,
    /**
     * @param {Object|ol.geom.Geometry} geom
     * @returns {number[]}
     */
    pointOnSurface,
    featureFromGeoJSON,
    featureToGeoJSON,
    transformStyleHash,
    transformStyle,
    createStyle,
    isClusterFeature,
    clusterExtent,
    createStyleFunction,
    pickFeatureProperties,
    setFeatureStyleName,
    getFeatureStyleName,
    getFeaturePrevStyleName
});

GIS.Map.OL3.Static( /** @lends GIS.Map.OL3 */ {
    // shared helper functions
    createTileUrlFunction: createTileUrlFunction,
    defineProjection,
    transformCoordinateProjection,
    transformExtentProjection,
    isEquivalentProjections,
    /**
     * @param {Object|ol.geom.Geometry} geom
     * @returns {number[]}
     */
    pointOnSurface,
    geomFromGeoJSON,
    featureFromGeoJSON,
    geomToGeoJSON,
    featureToGeoJSON,
    transformStyleHash,
    transformStyle,
    createStyle,
    isClusterFeature,
    clusterExtent,
    createStyleFunction,
    pickFeatureProperties,
    setFeatureStyleName,
    getFeatureStyleName,
    getFeaturePrevStyleName
});

// private methods
/**
 * Moves layer click listener according to it's z-index position.
 * @param {ol.layer.Layer} layer
 * @param {Array.<Object>} listeners
 * @private
 */
function clickListenerMove(layer, listeners)
{
    const index = _.findIndex(listeners, listener => listener.context === layer.get("glAdapterLayer"));
    const listener = listeners[index];

    if (listener) {
        listeners.splice(index, 1);
        listeners.unshift(listener);
    }
}

/**
 * @param {string} name
 * @param {ol.Object} obj
 * @param {string|string[]} evt
 * @param {function} callback
 * @param {Object|undefined} [context]
 * @returns {ol.events.Key}
 * @private
 * @this {GIS.Map.OL3}
 */
function listen(name, obj, evt, callback, context) {
    if (this.evtListeners[name]) {
        unlisten.call(this, name);
    }

    const listener = obj.on(evt, callback, context, true);

    this.evtListeners[name] = listener;

    return listener;
}

/**
 * @param {string|ol.events.Key} name
 * @returns {boolean}
 * @private
 * @this {GIS.Map.OL3}
 */
function unlisten(name) {
    const listener = _.isString(name) ? this.evtListeners[name] : ( _.isObject(name) ? name : null );

    name = _.isString(name) ? name : _.findKey(this.evtListeners, elem => elem === listener);

    if (listener && listener.target) {
        Observable.unByKey(listener);
        delete this.evtListeners[name];

        return true;
    }
    return false;
}

/**
 * @param collection
 * @returns {Function}
 * @private
 * @static
 */
function createMapCollectionRemoveCallback(collection) {
    return (
        /**
         * @param {ol.Collection.Event} e
         * @this {GIS.Map.OL3}
         */
        function(e) {
            var name = _.findKey(collection, e.element);

            if (name) {
                delete collection[name];
            }
        });
}

/**
 * @param collection
 * @returns {Function}
 * @private
 * @static
 */
function createMapCollectionAddCallback(collection) {
    return (
        /**
         * @param {ol.CollectionEvent} e
         * @this {GIS.Map.OL3}
         */
        function(e) {
            const name = Util.coalesce(e.element.name_, uuid.v4());

            e.element.name_ = name;

            switch (true) {
                case e.element instanceof Interaction:
                    if (this.interactions_[name]) {
                        this.removeInteraction(name);
                    }
                    break;
                case e.element instanceof Control:
                    if (this.controls_[name]) {
                        this.removeControl(name);
                    }
                    break;
                case e.element instanceof Overlay:
                    if (this.overlays_[name]) {
                        this.removeOverlay(name);
                    }
                    break;
                case e.element instanceof BaseLayer:
                    if (this.layers_[name]) {
                        this.removeLayer(name);
                    }
                    // move new added layer click handler to the top of stack
                    clickListenerMove(e.element, this.eventsSubscriptions['click']);
                    break;
            }

            collection[name] = e.element;
        });
}

/**
 * @param {Object|string} geoJson Geometry in GeoJSON
 * @param {ol.proj.ProjectionLike} [geometryProjection]
 * @param {ol.proj.ProjectionLike} [geoJSONProjection]
 * @return {ol.geom.Geometry}
 * @static
 * @public
 */
function geomFromGeoJSON(geoJson, geometryProjection, geoJSONProjection) {
    assert(!Util.isEmpty(geoJson), "empty `geoJson` argument", InvalidArgumentError);

    if (Util.isString(geoJson)) {
        geoJson = JSON.parse(geoJson);
    }

    assert(_.includes(GIS.Geometry.names(), geoJson.type), "unknown GeoJSON geometry", InvalidArgumentError);

    return (new GlonassJSONFormat()).readGeometry(geoJson, {
        dataProjection: geoJSONProjection,
        featureProjection: geometryProjection
    });
}

/**
 * @param {Object|string} geoJson
 * @param {ol.proj.ProjectionLike} [featureProjection]
 * @param {ol.proj.ProjectionLike} [geoJSONProjection]
 * @returns {ol.Feature}
 * @static
 * @public
 */
function featureFromGeoJSON(geoJson, featureProjection, geoJSONProjection) {
    const geoJsonFormat = new GeoJSONFormat();
    var feature;

    assert(!Util.isEmpty(geoJson), "empty `geoJson` argument", InvalidArgumentError);

    if (Util.isString(geoJson)) {
        geoJson = JSON.parse(geoJson);
    }

    if (_.includes(GIS.Geometry.names(), geoJson.type)) {
        geoJson = {
            type: "Feature",
            geometry: geoJson
        };
    }

    assert(_.includes(GIS.Geometry.names().concat(["FeatureCollection", "Feature"]), geoJson.type), "unknown GeoJSON feature", InvalidArgumentError);

    const options = {
        featureProjection,
        dataProjection: geoJSONProjection
    };

    switch (geoJson.type) {
        case "Feature":
            feature = geoJsonFormat.readFeature(geoJson, options);
            break;
        case "FeatureCollection":
            feature = geoJsonFormat.readFeatures(geoJson, options);
            break;
    }

    return feature;
}

/**
 * @param {ol.geom.Geometry|Object|string} geom
 * @param {ol.proj.ProjectionLike} [geometryProjection]
 * @param {ol.proj.ProjectionLike} [geoJSONProjection]
 * @param {boolean} [serialize]
 * @returns {Object|string}
 * @static
 * @public
 */
function geomToGeoJSON(geom, geometryProjection, geoJSONProjection, serialize) {
    var geoJson;

    assert(!Util.isEmpty(geom), "empty `geom` argument", InvalidArgumentError);

    if (!(geom instanceof Geometry)) {
        geom = geomFromGeoJSON(geom);
    } else {
        geom = geom.clone();
    }

    geoJson = (new GeoJSONFormat()).writeGeometryObject(geom, {
        dataProjection: geoJSONProjection,
        featureProjection: geometryProjection
    });

    if (serialize) {
        geoJson = JSON.stringify(geoJson);
    }

    return geoJson;
}

/**
 * @param {ol.Feature|Object|string} feature
 * @param {ol.proj.ProjectionLike} [featureProjection]
 * @param {ol.proj.ProjectionLike} [geoJSONProjection]
 * @param {boolean} [serialize]
 * @returns {Object|string}
 * @static
 * @public
 */
function featureToGeoJSON(feature, featureProjection, geoJSONProjection, serialize) {
    const geoJsonFormat = new GeoJSONFormat();
    var geoJson;

    assert(!Util.isEmpty(feature), "empty `feature` argument", InvalidArgumentError);

    if (!(feature instanceof Feature)) {
        feature = featureFromGeoJSON(feature, featureProjection, geoJSONProjection);
    } else {
        feature = feature.clone();
    }

    const options = {
        featureProjection,
        dataProjection: geoJSONProjection
    };

    geoJson = Util.isArray(feature) ? geoJsonFormat.writeFeaturesObject(feature, options) : geoJsonFormat.writeFeatureObject(feature, options);

    if (serialize) {
        geoJson = JSON.stringify(geoJson);
    }

    return geoJson;
}

/**
 * Defines custom projection.
 * @param {string|Array} code
 * @param {string} [def]
 * @param {Object} [options]
 * @static
 * @public
 */
function defineProjection(code, def, options) {
    GIS.Map.Base.defineProjection(code, def, options, (projDef) => {
        const projection = proj.get(projDef.code);

        if (!_.isEmpty(projDef.options)) {
            _.forEach(projDef.options, (value, option) => {
                const method = "set" + _.upperFirst(option);

                if (_.isFunction(method)) {
                    projection[method].call(projection, value);
                }
            });
        }
    });
}

/**
 * @param {Array.<Number>} coordinate
 * @param {string} source Source projection code
 * @param {string} destination Destination projection code
 * @returns {Array.<Number>}
 * @static
 * @public
 */
function transformCoordinateProjection(coordinate, source, destination) {
    return proj.transform(coordinate, source, destination);
}

/**
 * @param {Array.<Number>}extent
 * @param {string} source
 * @param {string} destination
 * @returns {Array.<Number>}
 * @static
 * @public
 */
function transformExtentProjection(extent, source, destination) {
    return proj.transformExtent(extent, source, destination);
}

/**
 * @param proj1
 * @param proj2
 * @returns {boolean}
 * @static
 * @public
 */
function isEquivalentProjections(proj1, proj2) {
    return proj.equivalent(proj1, proj2);
}

/**
 * @param {Object|ol.geom.Geometry} geom
 * @returns {number[]}
 * @static
 * @public
 */
function pointOnSurface(geom) {
    geom = geomToGeoJSON(geom);

    return GIS.Map.Base.pointOnSurface(geom);
}

const addPrefix = prefix => str => prefix + (prefix ? _.upperFirst(str) : str);

/**
 * @param {Object} glStyle
 * @param {string} [prefix]
 * @returns {ol.style.Fill|undefined}
 * @private
 */
function transformFillStyle(glStyle, prefix = '')
{
    const prefixKey = addPrefix(prefix);
    const keys = ['fillColor'].map(prefixKey);

    var fillStyle = _.reduce(Util.pickOptions(glStyle, keys), (result, value, name) => {
        name = _.lowerFirst(name.replace(new RegExp(prefixKey('fill')), ''));
        result[name] = value;

        return result;
    }, {});

    fillStyle = Util.pickOptions(fillStyle, _.keys(fillStyle));

    if (!_.isEmpty(fillStyle)) {
        return new FillStyle(fillStyle);
    }
}

/**
 * @param {Object} glStyle
 * @param {string} [prefix]
 * @returns {ol.style.Stroke|undefined}
 * @private
 */
function transformStrokeStyle(glStyle, prefix = '')
{
    const prefixKey = addPrefix(prefix);
    const keys = ["strokeColor", "strokeWidth", "strokeDash", "strokeCap", "strokeJoin"].map(prefixKey);

    var strokeStyle = _.reduce(Util.pickOptions(glStyle, keys), (result, value, name) => {
        switch (name) {
            case prefixKey("strokeColor"):
            case prefixKey("strokeWidth"):
                name = _.lowerFirst(name.replace(new RegExp(prefixKey('stroke')), ''));
                break;
            case prefixKey("strokeDash"):
            case prefixKey("strokeCap"):
            case prefixKey("strokeJoin"):
                name = 'line' + name.replace(new RegExp(prefixKey('stroke')), '');
                break;

        }

        result[name] = value;

        return result;
    }, {});

    strokeStyle = Util.pickOptions(strokeStyle, _.keys(strokeStyle));

    if (!_.isEmpty(strokeStyle)) {
        return new StrokeStyle(strokeStyle);
    }
}

/**
 * @param {Object} glStyle
 * @returns {ol.style.Text|undefined}
 * @private
 */
function transformTextStyle(glStyle)
{
    /*if (glStyle.title == null) {
        return;
    }*/

    var textStyle = {
        text: Util.validateOption(glStyle.title)
    };

    var fontSize = Util.validateOption(glStyle.textFontSize);
    fontSize = fontSize ? fontSize + "px" : undefined;

    var font = Util.validateOption([
        "normal",
        fontSize,
        Util.validateOption(glStyle.textFont)
    ].filter(_.identity).join(" "));

    _.assign(textStyle, Util.pickOptions(glStyle, ["textScale", "textRotation", "textOffsetX", "textOffsetY", "textAlign", "textText"]), {
        font,
        fill: _.assign(transformFillStyle(glStyle), transformFillStyle(glStyle, 'text')),
        stroke: _.assign(transformStrokeStyle(glStyle), transformStrokeStyle(glStyle, 'text')),
    });

    textStyle = Util.pickOptions(textStyle, _.keys(textStyle));

    if (!_.isEmpty(textStyle)) {
        return new TextStyle(textStyle);
    }
}

/**
 * @param {glStyle} glStyle
 * @returns {ol.style.Icon|ol.style.Circle|ol.style.RegularShape|undefined}
 * @private
 */
function transformImageStyle(glStyle)
{
    if (
        _.isEmpty(glStyle.iconUrl) && _.isEmpty(glStyle.iconImg) &&
        _.isEmpty(glStyle.iconPoints) && !Util.isNumeric(glStyle.iconRadius)
    ) {
        return;
    }

    var imageStyle;
    let StyleCtor

    if (!_.isEmpty(glStyle.iconUrl) || !_.isEmpty(glStyle.iconImg)) {
        // then create ol.style.Icon options
        let size, imgSize;

        size = glStyle.iconSize;
        imgSize = glStyle.iconImgSize;
        StyleCtor = IconStyle
        imageStyle = _.assign(glStyle, {
            type: "icon",
            anchor: Util.validateOption(glStyle.iconAnchor),
            anchorOrigin: Util.validateOption(glStyle.iconAnchorOrigin),
            color: Util.validateOption(glStyle.iconColor),
            offset: Util.validateOption(glStyle.iconOffset),
            offsetOrigin: Util.validateOption(glStyle.iconOffsetOrigin),
            opacity: Util.validateOption(glStyle.iconOpacity),
            scale: Util.validateOption(glStyle.iconScale),
            rotation: Util.validateOption(glStyle.iconRotation),
            size: Util.validateOption(size),
            imgSize: Util.validateOption(imgSize),
            src: Util.validateOption(glStyle.iconUrl),
            crossOrigin: "anonymous"
        });
    } else if (glStyle.iconPoints != null) {
        StyleCtor = RegularShapeStyle
        // create ol.style.RegularShape options
        imageStyle = _.assign(glStyle, {
            type: "regularShape",
            points: Util.validateOption(glStyle.iconPoints),
            radius: Util.validateOption(glStyle.iconRadius),
            radius1: Util.validateOption(glStyle.iconRadius1),
            radius2: Util.validateOption(glStyle.iconRadius2),
            angle: Util.validateOption(glStyle.iconAngle),
            rotation: Util.validateOption(glStyle.iconRotation)
        });
    } else {
        StyleCtor = CircleStyle
        // create ol.style.Circle options
        imageStyle = _.assign(glStyle, {
            type: "circle",
            radius: Util.validateOption(glStyle.iconRadius)
        });
    }

    _.assign(imageStyle, {
        fill: _.assign(transformFillStyle(glStyle), transformFillStyle(glStyle, 'icon')),
        stroke: _.assign(transformStrokeStyle(glStyle), transformStrokeStyle(glStyle, 'icon')),
        snapToPixel: true
    });

    imageStyle = Util.pickOptions(imageStyle, _.keys(imageStyle));

    if (!_.isEmpty(imageStyle)) {
        return new StyleCtor(imageStyle);
    }
}

function transformGeometryStyle (glStyle) {
    if (!_.isFunction(glStyle.geometry)) {
        return
    }

    return glStyle.geometry
}

/**
 * @param {glStyle} glStyle
 * @returns {ol.style.Style|undefined}
 * @static
 * @public
 */
function transformStyle(glStyle)
{
    /**
     * @type {ol.style.Options}
     */
    var ol3Style;

    if (glStyle) {
        ol3Style = {
            geometry: transformGeometryStyle(glStyle),
            text: transformTextStyle(glStyle),
            fill: transformFillStyle(glStyle),
            stroke: transformStrokeStyle(glStyle),
            image: transformImageStyle(glStyle),
            zIndex: Util.validateOption(glStyle.zIndex)
        };
    } else {
        ol3Style = {
            geometry: undefined,
            text: undefined,
            fill: undefined,
            stroke: undefined,
            image: undefined,
            zIndex: undefined
        };
    }

    ol3Style = Util.pickOptions(ol3Style, _.keys(ol3Style));

    if (!_.isEmpty(ol3Style)) {
        const style = new Style(ol3Style);

        style.geometryName = _.get(glStyle, 'geometryName');

        return style;
    }
}

/**
 * Converts Glonass layer style hash to inner ol3 style hash.
 * @param {glStyleHash} glStyleHash
 * @returns {ol3StyleHash}
 * @static
 * @public
 */
function transformStyleHash(glStyleHash)
{
    return _.transform(
        glStyleHash,
        /**
         * @param {ol3StyleHash} ol3StyleHash
         * @param {{ styles: glStyle[] }} glStyles
         * @param {string} styleName Style name
         */
        (ol3StyleHash, glStyles, styleName) => {
            if (glStyles == null) {
                return ol3StyleHash[ styleName ] = null // null style for hides
            }

            return ol3StyleHash[ styleName ] = _.map((glStyles && glStyles.styles || []), transformStyle)
        },
        {}
    );
}

/**
 * @param {olx.style.StyleOptions} options
 * @param {Function} [callback]
 * @returns {olx.style.StyleOptions}
 */
function prepareStyleOptions(options, callback) {
    let styleCtors = {
        circle: CircleStyle,
        icon: IconStyle,
        regularShape: RegularShapeStyle,
        text: TextStyle,
        fill: FillStyle,
        stroke: StrokeStyle,
    }

    return _.reduce(options, (result, options, name) => {
        const key = name;
        // change name to current class name
        if (name === "image") {
            name = options.type;
        }

        // recursively create options for composite style objects
        if (_.includes(["image", "fill", "stroke"], key) || key === "text" && _.isPlainObject(options)) {
            options = new styleCtors[_.upperFirst(name)](prepareStyleOptions(options, callback));
        } else {
            if (_.isFunction(callback)) {
                options = callback(options, key);
            }
        }

        result[key] = options;

        return result;
    }, {});
}

/**
 * @param {olx.style.StyleOptions} options
 * @param {Function} [callback]
 * @returns {ol.style.Style}
 * @static
 * @public
 */
function createStyle(options, callback) {
    return new Style(prepareStyleOptions(options, callback));
}

/**
 * @param {ol.layer.Base} vendorLayer
 * @returns {Function}
 */
function matchVendorFilter(vendorLayer)
{
    return function (layer) {
        return layer.getVendor() === vendorLayer;
    };
}

/**
 * @param {ol.Feature} feature
 * @returns {boolean}
 * @public
 * @static
 */
function isClusterFeature(feature) {
    return _.isArray(feature.get("features"));
}

/**
 * @param {ol.Feature} feature
 * @param {ol.proj.ProjectionLike} [srcProjection]
 * @param {ol.proj.ProjectionLike} [destProjection]
 * @returns {number[]}
 * @public
 * @static
 */
function clusterExtent(feature, srcProjection, destProjection) {
    return transformExtentProjection(
        extent.boundingExtent(
            _.map(
                feature.get("features"),
                feature => feature.getGeometry().getCoordinates()
            )
        ),
        srcProjection,
        destProjection
    );
}

/**
 * Extracts data properties of the feature.
 * @param {ol.Feature} feature
 * @returns {Object}
 * @static
 * @public
 */
function pickFeatureProperties(feature) {
    var exclude = [ GIS.Map.Base.STYLE_NAME_PROP, GIS.Map.Base.PREV_STYLE_NAME_PROP ];

    return _.omitBy(
        feature.getProperties(), (value, name) => _.isObject(value) && !_.isPlainObject(value) || _.includes(exclude, name)
    );
}

// http://stackoverflow.com/questions/14484787/wrap-text-in-javascript
function stringDivider(str, width, spaceReplacer) {
    if (str.length > width) {
        var p = width;
        while (p > 0 && (str[p] != ' ' && str[p] != '-')) {
            p--;
        }
        if (p > 0) {
            var left;
            if (str.substring(p, p + 1) == '-') {
                left = str.substring(0, p + 1);
            } else {
                left = str.substring(0, p);
            }
            var right = str.substring(p + 1);
            return left + spaceReplacer + stringDivider(right, width, spaceReplacer);
        }
    }
    return str;
}

/**
 * @param {glStyleHash} styleHash
 * @param {string} [cacheKey=base]
 * @returns {ol.style.StyleFunction|Function}
 * @public
 * @static
 */
function createStyleFunction(styleHash, cacheKey = 'styles')
{
    const dynamicProperties = ['title', 'fillColor', 'strokeColor', 'iconFillColor', 'iconStrokeColor', 'textFillColor',
                               'textStrokeColor'];

    const preCompileDynamicProperties = styleHash => {
        const founded = [];

        _.forEach(styleOptions, (option) => {
            _.forEach(styleHash, (styles, styleName) => {
                _.forEach((styles && styles.styles || []), (style, i) => {
                    const path = [ styleName, 'styles', i, option.name ];
                    var value = style[ option.name ];

                    if (isTemplate(value)) {
                        value = _.template(value);
                        style[ option.name ] = value;
                    }

                    founded.push({ name: path.join('.'), value });
                });
            });
        });

        return founded;
    };

    const foundDynamicProperties = preCompileDynamicProperties(_.cloneDeep(styleHash));

    // transform to ol3 style hash
    let olStyleHash;// = ( /** @type {ol3StyleHash} */ Data.Cache.cached(() => transformStyleHash(styleHash), cacheKey, 3600000));
    olStyleHash = transformStyleHash(_.cloneDeep(styleHash))

    return (
        /**
         * @param {ol.Feature} feature
         * @param {number} resolution
         * @returns {ol.style.Style[]|undefined}
         */
        function(feature, resolution) {
            // switch style name
            const item = pickFeatureProperties(feature);
            const styleName = getFeatureStyleName(feature);
            const geomName = GIS.Geometry.simpleName(GIS.Geometry[feature.getGeometry().getType()]);

            // apply dynamic styling
            // if (!_.isEmpty(foundDynamicProperties)) {
            //     const compiledStyleHash = _.cloneDeep(styleHash);
            //
            //     let subKey = Util.String.hash(_.map(foundDynamicProperties, ({ name, value }) => {
            //         if (_.isFunction(value)) {
            //             value = value({ item, data: item /* todo this is for old layers, remove later */ });
            //         }
            //
            //         _.set(compiledStyleHash, name, value);
            //
            //         return name + ':' + value;
            //     }).join('|'))
            //     const cachedStyles = Data.Cache.get(cacheKey)
            //     if (cachedStyles && cachedStyles[subKey]) {
            //         olStyleHash = cachedStyles[subKey]
            //     } else {
            //         olStyleHash = transformStyleHash(compiledStyleHash)
            //         Data.Cache.set(cacheKey, {
            //             ...cachedStyles || {},
            //             [subKey]: olStyleHash
            //         }, 3600000)
            //     }
            // }

            let styles = olStyleHash[styleName]

            if (!_.isEmpty(styles)) {
                styles = styles.filter((style) => style.geometryName === undefined || style.geometryName === geomName);
            }

            // partially apply dynamic styles
            if (!_.isEmpty(styles) && item.style) {
                styles = styles.slice()
                styles[0] = styles[0].clone()

                if (item.style.fillColor) {
                    let fill = styles[0].getFill()
                    if (!fill) {
                        fill = new FillStyle()
                        styles[0].setFill(fill)
                    }
                    fill.setColor(item.style.fillColor)

                    let icon = styles[0].getImage()
                    if (icon instanceof CircleStyle) {
                        icon.getFill().setColor(item.style.fillColor)
						icon = icon.clone()
						styles[0].setImage(icon);
                    }
                }
                if (item.style.strokeColor) {
                    let stroke = styles[0].getStroke()
                    if (!stroke) {
                        stroke = new StrokeStyle()
                        styles[0].setStroke(stroke)
                    }
                    stroke.setColor(item.style.strokeColor)

                    let icon = styles[0].getImage()
                    if (icon instanceof CircleStyle) {
                        icon.getStroke().setColor(item.style.strokeColor)
						icon = icon.clone()
						styles[0].setImage(icon);
                    }
                }
                if (item.style.title) {
                    let text = styles[0].getText()
                    if (!text) {
                        text = new TextStyle()
                        styles[0].setText(text)
                    }
                    text.setText(stringDivider(item.style.title, 16, '\n'))
                }
            }

            if (styles == null) { // null style, starting from ol4
                return [new Style({})]
            }

            return styles;
        }
    );
}

/**
 * @param {ol.Feature} feature
 * @param {string} [styleName=default]
 * @param {string | undefined} [prevStyleName]
 */
function setFeatureStyleName (feature, styleName = 'default', prevStyleName = undefined) {
    feature.setProperties({
        [ GIS.Map.Base.PREV_STYLE_NAME_PROP ]: prevStyleName || getFeatureStyleName(feature),
        [ GIS.Map.Base.STYLE_NAME_PROP ]: styleName
    })

    if (styleName === 'remove') {
        feature.setStyle(new Style({}))
    } else {
        feature.setStyle(null)
    }
}

/**
 * @param {ol.Feature} feature
 * @return {string}
 */
function getFeatureStyleName (feature) {
    return feature.get(GIS.Map.Base.STYLE_NAME_PROP) || 'default'
}

/**
 * @param {ol.Feature} feature
 * @return {string}
 */
function getFeaturePrevStyleName (feature) {
    return feature.get(GIS.Map.Base.PREV_STYLE_NAME_PROP) || getFeatureStyleName(feature)
}
